import React, { useState, useEffect } from 'react';
import { Globe, MapPin, Loader, Monitor, Smartphone, Tablet, Wifi, Cpu } from 'lucide-react';

const ServerInfo = ({ serverInfo, setServerInfo }) => {
  const [loading, setLoading] = useState(true);
  const [ipInfo, setIpInfo] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({
    type: 'Unknown',
    os: 'Unknown',
    browser: 'Unknown',
    screen: 'Unknown'
  });

  // Function to detect device type
  const detectDevice = () => {
    const userAgent = navigator.userAgent;
    let type = 'Unknown';
    let os = 'Unknown';
    let browser = 'Unknown';
    const screen = `${window.screen.width}x${window.screen.height}`;

    // Device type detection
    if (/iPad/.test(userAgent)) {
      type = 'Tablet';
    } else if (/Mobile|Android/.test(userAgent)) {
      type = 'Mobile';
    } else if (/Tablet/.test(userAgent)) {
      type = 'Tablet';
    } else {
      type = 'Desktop';
    }

    // OS detection
    if (/Windows NT 10/.test(userAgent)) {
      os = 'Windows 10/11';
    } else if (/Windows NT 6.3/.test(userAgent)) {
      os = 'Windows 8.1';
    } else if (/Mac/.test(userAgent)) {
      os = 'MacOS';
    } else if (/Linux/.test(userAgent)) {
      os = 'Linux';
    } else if (/Android/.test(userAgent)) {
      os = 'Android';
    } else if (/iOS|iPhone|iPad|iPod/.test(userAgent)) {
      os = 'iOS';
    } else if (/CrOS/.test(userAgent)) {
      os = 'Chrome OS';
    }

    // Browser detection
    if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) {
      browser = 'Chrome';
    } else if (/Firefox/.test(userAgent)) {
      browser = 'Firefox';
    } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
      browser = 'Safari';
    } else if (/Edg/.test(userAgent)) {
      browser = 'Edge';
    } else if (/Opera|OPR/.test(userAgent)) {
      browser = 'Opera';
    }

    return { type, os, browser, screen };
  };

  // Function to get device icon
  const getDeviceIcon = (type) => {
    switch (type) {
      case 'Mobile':
        return <Smartphone className="w-4 h-4 mr-1" />;
      case 'Tablet':
        return <Tablet className="w-4 h-4 mr-1" />;
      default:
        return <Monitor className="w-4 h-4 mr-1" />;
    }
  };

  useEffect(() => {
    const fetchIPInfo = async () => {
      try {
        setLoading(true);
        
        // Detect device info
        const deviceData = detectDevice();
        setDeviceInfo(deviceData);
        
        // Use multiple IP info services as backup
        const services = [
          'https://ipapi.co/json/',
          'https://ipwho.is/',
          'https://freeipapi.com/api/json',
          'https://api.ipify.org?format=json'
        ];

        let ipData = null;
        
        for (const service of services) {
          try {
            const response = await fetch(service, {
              signal: AbortSignal.timeout(5000)
            });
            if (response.ok) {
              ipData = await response.json();
              break;
            }
          } catch (error) {
            console.warn(`Failed to fetch from ${service}:`, error);
            continue;
          }
        }

        if (ipData) {
          const info = {
            ip: ipData.ip || ipData.query || 'Unknown',
            city: ipData.city || 'Unknown',
            country: ipData.country_name || ipData.country || 'Unknown',
            region: ipData.region || ipData.regionName || '',
            isp: ipData.org || ipData.isp || 'Unknown ISP',
            timezone: ipData.timezone || ''
          };
          
          setIpInfo(info);
          
          if (setServerInfo) {
            setServerInfo(prev => ({
              ...prev,
              ip: info.ip,
              location: `${info.city}, ${info.country}`,
              provider: info.isp,
              timezone: info.timezone
            }));
          }
        } else {
          throw new Error('All IP services failed');
        }
      } catch (error) {
        console.error('Failed to fetch IP info:', error);
        setIpInfo({
          ip: 'Unable to detect',
          city: 'Unknown',
          country: 'Unknown',
          isp: 'Unknown ISP',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
      } finally {
        setLoading(false);
      }
    };

    fetchIPInfo();
  }, [setServerInfo]);

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-6 bg-white dark:bg-gray-800 border border-[#1E9B4C]/20 dark:border-green-500/20">
        <h3 className="font-bold text-lg mb-4 flex items-center text-[#1E9B4C] dark:text-green-400">
          <Globe className="w-5 h-5 mr-2" />
          Device & Location Info
        </h3>
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 animate-spin text-[#1E9B4C] dark:text-green-400" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Detecting device and location...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-6 bg-white dark:bg-gray-800 border border-[#1E9B4C]/20 dark:border-green-500/20">
      <h3 className="font-bold text-lg mb-4 flex items-center text-[#1E9B4C] dark:text-green-400">
        <Globe className="w-5 h-5 mr-2" />
        Device & Location Info
      </h3>
      <div className="space-y-4">
        {/* Location Information */}
        <div className="space-y-3 text-sm">
          <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
            <MapPin className="w-4 h-4 mr-2 text-[#1E9B4C] dark:text-green-400" />
            Location Information
          </h4>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">IP Address:</span>
            <span className="font-mono text-[#1E9B4C] dark:text-green-400">{serverInfo.ip}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Location:</span>
            <div className="flex items-center text-right">
              <MapPin size={14} className="mr-1 flex-shrink-0 text-[#1E9B4C] dark:text-green-400" />
              <span className="text-gray-800 dark:text-gray-200">{serverInfo.location}</span>
            </div>
          </div>
          {ipInfo?.region && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Region:</span>
              <span className="text-gray-800 dark:text-gray-200">{ipInfo.region}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">ISP:</span>
            <div className="flex items-center">
              <Wifi size={14} className="mr-1 text-[#1E9B4C] dark:text-green-400" />
              <span className="text-gray-800 dark:text-gray-200">{ipInfo?.isp || serverInfo.provider}</span>
            </div>
          </div>
          {ipInfo?.timezone && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Timezone:</span>
              <span className="text-gray-800 dark:text-gray-200">{ipInfo.timezone}</span>
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="border-t border-gray-300 dark:border-gray-600 my-3"></div>

        {/* Device Information */}
        <div className="space-y-3 text-sm">
          <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center">
            <Cpu className="w-4 h-4 mr-2 text-[#1E9B4C] dark:text-green-400" />
            Device Information
          </h4>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Device Type:</span>
            <div className="flex items-center">
              {getDeviceIcon(deviceInfo.type)}
              <span className="text-gray-800 dark:text-gray-200">{deviceInfo.type}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Operating System:</span>
            <span className="text-gray-800 dark:text-gray-200">{deviceInfo.os}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Browser:</span>
            <span className="text-gray-800 dark:text-gray-200">{deviceInfo.browser}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Screen Resolution:</span>
            <span className="text-gray-800 dark:text-gray-200">{deviceInfo.screen}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerInfo;